import React, { memo, useEffect, useMemo } from 'react';
import styles from "./styles.module.scss";
import { ThirtyText } from "@/app/components/texts";
import { t } from "i18next";
import { s } from "@/app/utils/s";
import CircularProgressIndicator from "@/app/components/circular_progress_indicator";
import { useNavigate } from "react-router-dom";
import { route } from "@/app/routes/AppRoutes";
import Breadcrumbs from "@/app/components/breadcrumbs";
import GameRowWidget from "@/app/components/game_row_widget";
import { OpenMatchModelResponse, OpenMatchStatusEnum } from "@/app/api/open_match";
import useMatches from "./slice";
import { useMediaQuery, handleMatchClick } from "@/app/utils/custom_function";
import { Tips } from "@/app/components/tips/widget";

const MatchesPage = () => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const navigate = useNavigate();
  const { state, fetchAllMatches, toggleFilters } = useMatches();

  useEffect(() => {
    fetchAllMatches();
  }, [fetchAllMatches]);

  const onMatchClick = (match: OpenMatchModelResponse) => {
    handleMatchClick(match, navigate);
  };

  // Filter labels mapping
  const filterLabels = useMemo(() => {
    const statusToLabelMap = {
      [OpenMatchStatusEnum.NOT_STARTED]: t(s.waitingMatches),
      [OpenMatchStatusEnum.STARTED]: t(s.inProgressMatches),
      [OpenMatchStatusEnum.CANCELED]: t(s.canceledMatches),
      [OpenMatchStatusEnum.ARCHIVED]: t(s.completedMatches),
    };
    return Object.values(OpenMatchStatusEnum).map(status => statusToLabelMap[status]);
  }, []);

  const handleFilterChange = (selectedLabels: string[]) => {
    const statusToLabelMap = {
      [OpenMatchStatusEnum.NOT_STARTED]: t(s.waitingMatches),
      [OpenMatchStatusEnum.STARTED]: t(s.inProgressMatches),
      [OpenMatchStatusEnum.CANCELED]: t(s.canceledMatches),
      [OpenMatchStatusEnum.ARCHIVED]: t(s.completedMatches),
    };
    
    const selectedStatuses = Object.entries(statusToLabelMap)
      .filter(([_, label]) => selectedLabels.includes(label))
      .map(([status, _]) => status as OpenMatchStatusEnum);
    
    toggleFilters(selectedStatuses);
  };

  // Get initial filter values for Tips component
  const initialFilterValues = useMemo(() => {
    const statusToLabelMap = {
      [OpenMatchStatusEnum.NOT_STARTED]: t(s.waitingMatches),
      [OpenMatchStatusEnum.STARTED]: t(s.inProgressMatches),
      [OpenMatchStatusEnum.CANCELED]: t(s.canceledMatches),
      [OpenMatchStatusEnum.ARCHIVED]: t(s.completedMatches),
    };
    return state.activeFilters.map(status => statusToLabelMap[status]);
  }, [state.activeFilters]);

  // Helper function to render a section of matches
  const renderMatchesSection = (title: string, matches: OpenMatchModelResponse[] | any) => {
    const list: OpenMatchModelResponse[] = Array.isArray(matches) ? matches : [];
    if (list.length === 0) return null;

    return (
      <div className={styles.matchesSection}>
        <ThirtyText text={title} />
        <div className={styles.matchesList}>
          {list.map((match) => (
            <GameRowWidget 
              key={match.id} 
              match={match} 
              onClick={onMatchClick}
              isDesktop={isDesktop}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.matchesPage}>
      <Breadcrumbs items={state.breadcrumbs} />
      
      {/* Filter Tips */}
      <div className={styles.filtersContainer}>
        <Tips
          tips={filterLabels}
          initialValues={initialFilterValues}
          onChange={handleFilterChange}
        />
      </div>
      
      {state.isLoading && (
        <div className={styles.loading}>
          <CircularProgressIndicator isGreen />
        </div>
      )}
      
      {state.error && (
        <div className={styles.error}>
          <ThirtyText text={state.error} />
        </div>
      )}
      
      {!state.isLoading && !state.error && (
        state.waitingMatches.length === 0 &&
        state.inProgressMatches.length === 0 &&
        state.canceledMatches.length === 0 &&
        state.completedMatches.length === 0
      ) && (
        <div className={styles.centered}>
          <ThirtyText text={t(s.noMatches)} />
        </div>
      )}
      
      {!state.isLoading && !state.error && (
        <>
          {state.activeFilters.includes(OpenMatchStatusEnum.NOT_STARTED) && 
            renderMatchesSection(t(s.waitingMatches), state.waitingMatches)}
          {state.activeFilters.includes(OpenMatchStatusEnum.STARTED) && 
            renderMatchesSection(t(s.inProgressMatches), state.inProgressMatches)}
          {state.activeFilters.includes(OpenMatchStatusEnum.CANCELED) && 
            renderMatchesSection(t(s.canceledMatches), state.canceledMatches)}
          {state.activeFilters.includes(OpenMatchStatusEnum.ARCHIVED) && 
            renderMatchesSection(t(s.completedMatches), state.completedMatches)}
        </>
      )}
    </div>
  );
};

export default memo(MatchesPage);
